"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/hooks/useAuth";
import { apiFetch } from "@/lib/api/client";
import SpiritCard from "@/components/spirit/SpiritCard";
import PageBackground from "@/components/ui/PageBackground";
import SkillTreeGoals from "@/components/ui/SkillTreeGoals";
import type { Spirit } from "@/lib/types/spirit";

export default function Home() {
  const { userData, user, loading, isLoggedIn, logout } = useAuth();
  const router = useRouter();
  const [spirits, setSpirits] = useState<Spirit[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !isLoggedIn) {
      router.replace("/login");
    }
  }, [loading, isLoggedIn, router]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const token = await user.getIdToken();
      setToken(token);
      const [allSpirits, histories] = await Promise.all([
        apiFetch<Spirit[]>("/api/spirits", token).catch(() => []),
        apiFetch<{ readCount: number }[]>("/api/read-history", token).catch(() => []),
      ]);
      const activeIds = userData?.activeSpirits ?? [];
      setSpirits((allSpirits || []).filter((s) => activeIds.includes(s.id)));
      setUnreadCount((histories || []).filter((h) => h.readCount === 0).length);
    })();
  }, [user, userData?.activeSpirits]);

  if (loading || !isLoggedIn || !userData) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-[var(--foreground)] opacity-60">読み込み中...</p>
      </div>
    );
  }

  const totalPoints = userData.magicPoints.free + userData.magicPoints.paid;

  return (
    <div className="flex min-h-screen flex-col pb-20">
      <PageBackground page="home" overrideId={userData?.selectedBackgrounds?.home} />
      {/* ヘッダー */}
      <header className="flex items-center justify-between border-b border-violet-500/20 px-6 py-2">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-700 text-base text-white">
            ∞
          </div>
          <h1 className="text-base font-bold text-[var(--foreground)]">
            無限図書館
          </h1>
        </div>
        <button
          onClick={logout}
          className="rounded-lg px-3 py-1.5 text-sm text-[var(--foreground)] opacity-60 transition-opacity hover:opacity-100"
        >
          ログアウト
        </button>
      </header>

      {/* メインコンテンツ */}
      <main className="flex flex-1 flex-col items-center gap-3 px-6 py-4">
        {/* ユーザー情報 */}
        <div className="w-full max-w-md rounded-2xl border border-white/15 bg-[var(--background)]/65 backdrop-blur-sm px-4 py-2">
          <p className="text-sm text-[var(--foreground)] opacity-90">
            ようこそ、
          </p>
          <p className="text-lg font-bold text-[var(--foreground)]">
            {userData.displayName}
          </p>
        </div>

        {/* 魔力ポイント */}
        <div className="w-full max-w-md rounded-2xl border border-white/15 bg-[var(--background)]/65 backdrop-blur-sm px-4 py-2.5">
          <p className="text-sm text-[var(--foreground)] opacity-60">魔力ポイント</p>
          <p className="text-2xl font-bold text-[var(--foreground)]">
            {totalPoints}
            <span className="ml-1 text-sm font-normal opacity-60">pt</span>
          </p>
          {userData.magicPoints.paid > 0 && (
            <p className="text-xs text-[var(--foreground)] opacity-50">
              (無償: {userData.magicPoints.free} / 有償: {userData.magicPoints.paid})
            </p>
          )}
        </div>

        {/* 解放目標（スタンプラリー） */}
        {token && <SkillTreeGoals token={token} />}

        {/* 未読通知 */}
        {unreadCount > 0 && (
          <button
            onClick={() => router.push("/shelf")}
            className="w-full max-w-md rounded-xl border border-amber-500/50 bg-amber-500/25 px-4 py-2 text-center text-sm text-amber-400 transition-all hover:bg-amber-500/35 backdrop-blur-sm"
          >
            未読の本が{unreadCount}冊あります
          </button>
        )}

        {/* 手持ち精霊 */}
        {spirits.length > 0 ? (
          <div className="w-full max-w-md">
            <p className="mb-1 text-sm text-[var(--foreground)] opacity-90">
              手持ち精霊（{spirits.length}/{userData.handSlotCount ?? 1}）
            </p>
            <div className="flex flex-col gap-1.5">
              {spirits.map((s) => (
                <SpiritCard
                  key={s.id}
                  spirit={s}
                  onClick={() => router.push(`/spirit?spiritId=${s.id}`)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="w-full max-w-md rounded-2xl border border-white/15 bg-[var(--background)]/65 backdrop-blur-sm px-4 py-3 text-center">
            <p className="text-sm text-[var(--foreground)] opacity-60">
              手持ち精霊が設定されていません
            </p>
            <button
              onClick={() => router.push("/spirit")}
              className="mt-2 rounded-lg border border-white/20 bg-[var(--foreground)]/10 px-4 py-1.5 text-sm font-medium text-[var(--foreground)] transition-all hover:bg-[var(--foreground)]/20"
            >
              獲得精霊から選ぶ
            </button>
          </div>
        )}

        {/* アクションボタン */}
        <div className="flex w-full max-w-md flex-col gap-2">
          <button
            onClick={() => router.push("/backgrounds")}
            className="rounded-xl border border-white/15 bg-[var(--background)]/65 backdrop-blur-sm px-5 py-2.5 text-left text-[var(--foreground)] transition-all hover:bg-[var(--background)]/80 active:scale-[0.98]"
          >
            <span className="text-base font-medium">🏛️ 図書館を選ぶ</span>
            <span className="ml-2 text-sm opacity-60">— 読書する場所を変える</span>
          </button>
        </div>
      </main>
    </div>
  );
}
